import { type FC, useState } from 'react';
import styles from './NftPdpTabs.scss';
import { FormattedMessage } from 'dibs-react-intl';
import { TabList } from 'dibs-elements/exports/TabList';
import { TabProvider } from 'dibs-elements/exports/TabProvider';
import { TabPanels } from 'dibs-elements/exports/TabPanels';
import { graphql, useFragment } from 'react-relay';
import { NftPdpHistory } from '../NftPdpHistory/NftPdpHistory';
import { NftPdpOwners } from '../NftPdpOwners/NftPdpOwners';
import { type NftPdpTabs_viewer$key } from './__generated__/NftPdpTabs_viewer.graphql';
import { type NftPdpTabs_nftItem$key } from './__generated__/NftPdpTabs_nftItem.graphql';

const OWNERS_TAB = 'ownersTab';
const HISTORY_TAB = 'historyTab';

type Props = {
    viewer: NftPdpTabs_viewer$key;
    nftItem: NftPdpTabs_nftItem$key;
};

export const NftPdpTabs: FC<Props> = ({ viewer: viewerRef, nftItem: nftItemRef }) => {
    const [activeTab, setActiveTab] = useState(HISTORY_TAB);
    const viewer = useFragment(
        graphql`
            fragment NftPdpTabs_viewer on Viewer {
                nftBalancesByItem(nftId: $nftId) {
                    chainId
                    ownerAddress {
                        address
                        publicProfile {
                            displayName
                        }
                    }
                    balance
                }
                ...NftPdpOwners_viewer
                ...NftPdpHistory_viewer
            }
        `,
        viewerRef
    );
    const nftItem = useFragment(
        graphql`
            fragment NftPdpTabs_nftItem on NftItem {
                creatorAddress {
                    address
                }
                ...NftPdpOwners_nftItem
                ...NftPdpHistory_nftItem
            }
        `,
        nftItemRef
    );

    return (
        <div className={styles.tabContainer}>
            <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
                <TabList fullWidth indicatorPadding="xSmall">
                    {[
                        [
                            OWNERS_TAB,
                            <span
                                key="nft-pdp-tab-owners"
                                className={styles.tabLabel}
                                role="button"
                                tabIndex={0}
                            >
                                <FormattedMessage id="nft.pdp.tab.owners" defaultMessage="Owners" />
                            </span>,
                        ],
                        [
                            HISTORY_TAB,
                            <span
                                key="nft-pdp-tab-history"
                                className={styles.tabLabel}
                                role="button"
                                tabIndex={0}
                            >
                                <FormattedMessage
                                    id="nft.pdp.tab.history"
                                    defaultMessage="History"
                                />
                            </span>,
                        ],
                    ]}
                </TabList>
                <TabPanels renderOnInactive>
                    {[
                        [OWNERS_TAB, <NftPdpOwners nftItem={nftItem} viewer={viewer} />],
                        [
                            HISTORY_TAB,
                            <NftPdpHistory nftItem={nftItem} viewer={viewer} hasEditions />,
                        ],
                    ]}
                </TabPanels>
            </TabProvider>
            <div className={styles.mobileSeparatorContainer}>
                <div className={styles.mobileSeparator} />
            </div>
        </div>
    );
};
